import axios, { AxiosResponse } from "axios";
import { Feature } from "ol";
import { EsriJSON } from "ol/format";
import { IQuery } from "../Forms/Drawing/Search";
import { ISummaryParams } from "../Registration/SummarySection";
import { config, defaultOption, proxyUrl } from "./config";
import { AttachmentType, PrintType } from "./enums";
import { getMultiPolygonGeometry } from "./utils";

export interface IEsriFieldInfo {
    alias: string;
    domain: IEsriDomain;
    editable: boolean;
    name: string;
    nullable: boolean;
    type: string;
}

export interface IEsriDomain {
    type: string;
    name: string;
    description: string;
    codedValues: IEsriCodedValue[];
    mergePolicy: string;
}

export interface IEsriCodedValue {
    code: string | number;
    name: string;
}

export interface IObjectIDs {
    objectId: number;
    globalId: string;
}

export interface IAttachmentPath {
    type: AttachmentType;
    url: string;
}
export interface IAttachmentFile {
    type: AttachmentType;
    endpoint: string;
    resourceId: string;
}

export interface ISearchParams {
    f: string;
    where: string;
    outFields: string;
}

const esrijsonFormat = new EsriJSON();

const fieldInfoCache: Record<number, IEsriFieldInfo[]> = {};

export const fetchConfigJson = () => axios.get("config/config.json").then((resp) => resp.data);

export const fetchFieldInfo = async (): Promise<IEsriFieldInfo[]> => {
    const params = {
        f: "json",
        returnGeometry: true,
        where: "1=1",
        outFields: "*",
        outSR: "3059"
    };

    return axios
        .get(proxyUrl + "proxy/10e05deb539a422083b2df9769523faa/3", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const info = response.data.fields;
            addDefaultCodedValuesOption(info);
            fieldInfoCache[0] = info;
            return info;
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};
export const getFieldInfo = (): IEsriFieldInfo[] => fieldInfoCache[0];

const addDefaultCodedValuesOption = (info: IEsriFieldInfo[]) => {
    config.forms.eventForm.fields.map((inputField) => {
        const currentFieldInfo = info.find((item) => item.name === inputField.field);
        if (currentFieldInfo && currentFieldInfo.domain && currentFieldInfo.domain.codedValues) {
            currentFieldInfo.domain.codedValues.unshift(defaultOption);
        }
    });
};

const fixData = (features: Feature[], fieldInfos: IEsriFieldInfo[], service: string) => {
    const decimalFields = getDecimalFields(fieldInfos);

    for (const feature of features) {
        feature.set("service", service, true);
        if (!decimalFields?.length) {
            continue;
        }
        for (const fieldName of decimalFields) {
            const value = feature.get(fieldName);
            if (value !== null) {
                feature.set(fieldName, parseFloat((value as number).toFixed(8)), true);
            }
        }
    }
};

const getDecimalFields = (fieldInfos: IEsriFieldInfo[]): string[] => {
    return fieldInfos
        .filter((f) => f.type === "esriFieldTypeDouble" || f.type === "esriFieldTypeSingle")
        .map((f) => f.name);
};

export const updateFeatures = async (features: Feature[], layerId?: string) => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("g-recaptcha-response", recaptchaToken);
    queryString.append(
        "features",
        "[" + features.map((feature) => esrijsonFormat.writeFeature(feature)).toString() + "]"
    );

    return axios
        .post(
            proxyUrl + "proxy/recaptcha-8aa8cde7a8a3486e887ae5303a285950/" + (layerId || 0) + "/updateFeatures",
            queryString
        )
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response.data.updateResults;
        })
        .catch((err) => {
            console.error(err);
            return err.response;
        });
};

export const requestObjectIds = async (features: Feature[], layerId?: string): Promise<IObjectIDs[] | any> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("g-recaptcha-response", recaptchaToken);
    queryString.append(
        "features",
        "[" + features.map((feature) => esrijsonFormat.writeFeature(feature)).toString() + "]"
    );

    return axios
        .post(
            proxyUrl + "proxy/recaptcha-8aa8cde7a8a3486e887ae5303a285950/" + (layerId || 0) + "/addFeatures",
            queryString
        )
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const ids: IObjectIDs[] = response.data.addResults.map((res) => ({
                objectId: res.objectId,
                globalId: res.globalId
            }));
            return ids;
        })
        .catch((err) => err.response);
};

export const uploadFile = async (file: File, id: string): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("RelatedObjectId", id);
    formData.append("FieldName", config.upload.fieldName);
    formData.append("LayerName", config.upload.layerName);
    formData.append("g-recaptcha-response", recaptchaToken);

    return axios
        .post(config.upload.url, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const printPdf = async (data: ISummaryParams, printType: PrintType): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("g-recaptcha-response", recaptchaToken);
    formData.append("data", JSON.stringify(data));
    printType === PrintType.Path && formData.append("f", "json");

    return axios
        .post(
            config.print.url + config.print.endpoint,
            formData,
            printType === PrintType.File && {
                responseType: "blob",
                headers: {
                    Accept: "application/pdf"
                }
            }
        )
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

export const sendEmail = async (
    to: string,
    attachments: IAttachmentPath[] | IAttachmentFile[]
): Promise<AxiosResponse> => {
    const recaptchaToken = await getCaptchaToken();
    if (!recaptchaToken) {
        return undefined;
    }

    const formData = new FormData();
    formData.append("To", to);
    formData.append("Subject", config.email.subject);
    formData.append("Body", config.email.body);
    formData.append("g-recaptcha-response", recaptchaToken);

    if (attachments && attachments.length) {
        attachments.map((a, i) => {
            a.type && formData.append("ExternalAttachments[" + i + "].type", a.type);
            a.url && formData.append("ExternalAttachments[" + i + "].url", a.url);
            a.endpoint && formData.append("ExternalAttachments[" + i + "].endpoint", a.endpoint);
            a.resourceId && formData.append("ExternalAttachments[" + i + "].resourceId", a.resourceId);
        });
    }

    return axios
        .post(config.email.url, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            console.error(err);
            return { status: 0 } as AxiosResponse;
        });
};

export const executeQuery = async ({ service, url, layerId, params }: IQuery): Promise<Feature[]> => {
    return axios
        .get(url + "/" + layerId + "/query", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const features = esrijsonFormat.readFeatures(response.data);
            features?.length && fixData(features, response.data.fields, service);

            return features;
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};

const getCaptchaToken = (): Promise<string> => {
    return new Promise((resolve) => {
        //@ts-ignore
        window.grecaptcha.ready(() => {
            //@ts-ignore
            window.grecaptcha
                .execute(config.reCaptchaSiteKey, { action: "submit" })
                .then((token) => resolve(token))
                .catch(() => resolve(null));
        });
    });
};

export const getLVMForestry = async (features: Feature[]): Promise<AxiosResponse> => {
    const jsonData = {
        geometry: getMultiPolygonGeometry(features),
        GeometryType: "esriGeometryPolygon",
        fields: ["LVMFORESTRYCODE", "LVMFORESTRYNAME"]
    };

    return axios
        .post(config.geometryService.url + config.geometryService.endpoint, jsonData, {
            headers: { "Content-Type": "application/json" }
        })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const getIntersectedForestryCount = async (features: Feature[]): Promise<AxiosResponse> => {
    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("outFields", "*");
    queryString.append("geometryType", "esriGeometryPolygon");
    queryString.append("spatialRel", "esriSpatialRelIntersects");
    queryString.append("returnCountOnly", "true");
    queryString.append("geometry", JSON.stringify(getMultiPolygonGeometry(features)));

    return axios
        .post(proxyUrl + "proxy/13d020edd12a4c44894a782aea9527c2/3/query", queryString)
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};

export const getIntersectedForestries = async (features: Feature[]): Promise<AxiosResponse> => {
    const queryString = new URLSearchParams();
    queryString.append("f", "json");
    queryString.append("outFields", "*");
    queryString.append("geometryType", "esriGeometryPolygon");
    queryString.append("spatialRel", "esriSpatialRelIntersects");
    queryString.append("returnGeometry", "false");
    queryString.append("geometry", JSON.stringify(getMultiPolygonGeometry(features)));

    return axios
        .post(proxyUrl + "proxy/13d020edd12a4c44894a782aea9527c2/3/query", queryString)
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response;
        })
        .catch((err) => {
            console.error(err);
            return undefined;
        });
};
